import { loadEngineState, resolveFlagConfig, resolveUnitRuleForPreparedRow } from './mcEngineData';
import { hasZeroCostVarianceFlagBoundary, resolveCostVarianceFlagState } from './mcFlagging';

function numberValue(value, fallback = 0) {
  if (value === null || value === undefined || value === '') return fallback;
  const parsed = Number(String(value).replace(/,/g, '.'));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function positiveUnitValue(value, fallback = 1) {
  const parsed = numberValue(value, fallback);
  return parsed > 0 ? parsed : fallback;
}

function lineCost(line = {}, meta = {}, record = {}) {
  const direct = line?.effectiveCost ?? line?.unitCost ?? line?.price ?? line?.retail_price;
  if (direct !== null && direct !== undefined && direct !== '' && Number.isFinite(Number(direct))) {
    return Number(direct);
  }
  const scope = meta?.storeKey || meta?.storeName || meta?.store || record?.storeKey || record?.storeName || record?.store || 'ALL';
  const engine = loadEngineState(scope) || loadEngineState('ALL') || {};
  const sourceCost = numberValue(line?.sourceCost ?? line?.importedCost ?? line?.originalCost ?? line?.cost, 0);
  const rule = resolveUnitRuleForPreparedRow(line?.product || '', line?.category || '', engine?.config || {});
  const priceUnitSize = positiveUnitValue(rule?.priceUnitSize, positiveUnitValue(line?.priceUnitSize, 1));
  return Number((rule?.priceBasis === 'base' ? sourceCost : sourceCost / priceUnitSize).toFixed(2));
}

function deriveLedgerMetrics(lines = [], meta = {}, record = {}) {
  let completedLines = 0;
  let flaggedCount = 0;
  let costExposure = 0;
  const scope = meta?.storeKey || meta?.storeName || meta?.store || record?.storeKey || record?.storeName || record?.store || 'ALL';
  const flagConfig = resolveFlagConfig(scope);

  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const touched = line?.counted !== '' && line?.counted !== undefined && line?.counted !== null
      || line?.waste !== '' && line?.waste !== undefined && line?.waste !== null
      || Boolean(String(line?.note || line?.notes || '').trim());
    if (!touched) return;

    completedLines += 1;
    const counted = numberValue(line?.counted, 0);
    const waste = numberValue(line?.waste, 0);
    const systemSoh = numberValue(line?.systemSoh ?? line?.systemSOH ?? line?.soh, 0);
    const variance = counted + waste - systemSoh;
    const persistedExposure = line?.costVariance ?? line?.finalCostVariance ?? line?.varianceValue;
    const exposure = persistedExposure !== null && persistedExposure !== undefined && persistedExposure !== '' && Number.isFinite(Number(persistedExposure))
      ? Number(Number(persistedExposure).toFixed(2))
      : Number((variance * lineCost(line, meta, record)).toFixed(2));
    costExposure += exposure;

    const noteFlagged = Boolean(String(line?.note || line?.notes || '').trim());
    const explicitFlagged = Boolean(line?.flagged === true || line?.isFlagged === true);
    const { negativeFlagged, positiveFlagged } = resolveCostVarianceFlagState(exposure, flagConfig);
    if (explicitFlagged || noteFlagged || negativeFlagged || positiveFlagged) flaggedCount += 1;
  });

  return {
    totalLines: Array.isArray(lines) ? lines.length : 0,
    completedLines,
    flaggedCount,
    costExposure: Number(costExposure.toFixed(2)),
  };
}

function ledgerComplete(meta = {}) {
  const status = String(meta?.status || '').trim().toLowerCase();
  const lifecycle = String(meta?.lifecycleState || '').trim().toLowerCase();
  return Boolean(meta?.sectionCompletedAt || meta?.completedAt || status === 'section complete' || status === 'completed' || lifecycle === 'section-complete' || lifecycle === 'complete');
}

function ledgerVerified(meta = {}) {
  return Boolean(
    meta?.verifiedAt ||
    meta?.verification?.verifiedAt ||
    String(meta?.verificationStatus || meta?.verification?.status || '').trim().toLowerCase() === 'verified'
  );
}

export function projectDashboardSessionFromLedger(record = {}, ledgerValue = null) {
  if (!record?.sessionId || !ledgerValue) return null;
  let ledger = null;
  try { ledger = typeof ledgerValue === 'string' ? JSON.parse(ledgerValue) : ledgerValue; } catch (_) { return null; }
  if (!ledger || !Array.isArray(ledger?.lines)) return null;

  const meta = ledger?.meta || {};
  const derived = deriveLedgerMetrics(ledger.lines, meta, record);
  const metricScope = meta?.storeKey || meta?.storeName || meta?.store || record?.storeKey || record?.storeName || record?.store || 'ALL';
  const metricFlagConfig = resolveFlagConfig(metricScope);
  const completed = ledgerComplete(meta);
  const verified = ledgerVerified(meta);
  const frozen = completed && meta?.sessionSummary && typeof meta.sessionSummary === 'object' ? meta.sessionSummary : null;
  const metrics = frozen
    ? {
        totalLines: numberValue(frozen.totalLines, derived.totalLines),
        completedLines: numberValue(frozen.completedLines, derived.completedLines),
        flaggedCount: hasZeroCostVarianceFlagBoundary(metricFlagConfig)
          ? derived.flaggedCount
          : numberValue(frozen.flaggedCount ?? frozen.flaggedItems ?? frozen.flaggedProducts, derived.flaggedCount),
        costExposure: numberValue(frozen.costExposure ?? frozen.exposure ?? frozen.finalExposure, derived.costExposure),
      }
    : derived;

  return {
    ...record,
    ...metrics,
    status: completed ? 'Completed' : 'Active',
    sectionCompletedAt: meta.sectionCompletedAt || meta.completedAt || null,
    completedAt: meta.completedAt || meta.sectionCompletedAt || null,
    verificationStatus: verified ? 'verified' : 'unverified',
    verifiedAt: verified ? (meta.verifiedAt || meta.verification?.verifiedAt || '') : '',
    verification: {
      ...(meta.verification || {}),
      status: verified ? 'verified' : 'unverified',
      verifiedAt: verified ? (meta.verifiedAt || meta.verification?.verifiedAt || '') : '',
    },
    storeName: meta.storeName || meta.store || record.storeName || record.store,
    store: meta.store || meta.storeName || record.store || record.storeName,
    storeKey: meta.storeKey || record.storeKey,
    siteName: meta.siteName || record.siteName,
    siteId: meta.siteId || record.siteId,
    cycleId: meta.cycleId || meta.cycleKey || record.cycleId || record.cycleKey,
    cycleName: meta.cycleName || meta.cycle?.name || record.cycleName,
    sectionId: meta.sectionId || record.sectionId,
    sectionName: meta.sectionName || record.sectionName,
    startedBy: meta.startedBy || record.startedBy,
    startedAt: meta.startedAt || record.startedAt,
    operationalDate: meta.operationalDate || meta.businessDate || meta.countDate || record.operationalDate || record.businessDate || record.countDate || '',
    updatedAt: meta.lastEditedAt || ledger.savedAt || record.updatedAt,
    tenantOrgId: meta.tenantOrgId || meta.organizationId || meta.orgId || record.tenantOrgId || record.organizationId || record.orgId,
    orgId: meta.orgId || meta.organizationId || meta.tenantOrgId || record.orgId || record.organizationId || record.tenantOrgId,
    countAuthority: 'ledger',
    dashboardLedgerReady: true,
  };
}
